import { useState } from "react";
import type { SyncState, DeviceInfo } from "../hooks/useDeviceSync";

function DeviceIcon({ type }: { type: string | null }) {
  if (type === "phone") return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  );
  if (type === "tablet") return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function SyncStatusIndicator({ syncState, compact }: { syncState: SyncState; compact?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const statusColor = syncState.isConnected
    ? syncState.isSyncing ? "#FBBF24" : "#34D399"
    : "#EF4444";

  const statusText = syncState.isConnected
    ? syncState.isSyncing ? "Syncing..." : "Synced"
    : "Offline";

  if (compact) {
    return (
      <div
        data-testid="sync-status-compact"
        className="flex items-center gap-1.5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        title={`${statusText} | ${syncState.onlineCount} device${syncState.onlineCount !== 1 ? "s" : ""} online`}
      >
        <div className="relative">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
          {syncState.isSyncing && (
            <div
              className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping"
              style={{ backgroundColor: statusColor, opacity: 0.4 }}
            />
          )}
        </div>
        {syncState.onlineCount > 1 && (
          <span className="text-xs text-white/60">{syncState.onlineCount}</span>
        )}
      </div>
    );
  }

  return (
    <div data-testid="sync-status-indicator" className="relative">
      <button
        data-testid="button-sync-toggle"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="relative">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColor }} />
          {syncState.isSyncing && (
            <div
              className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping"
              style={{ backgroundColor: statusColor, opacity: 0.4 }}
            />
          )}
        </div>
        <span className="text-xs text-white/70">{statusText}</span>
        {syncState.onlineCount > 1 && (
          <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: "rgba(139,92,246,0.3)", color: "#C4B5FD" }}>
            {syncState.onlineCount} devices
          </span>
        )}
      </button>

      {expanded && (
        <div
          data-testid="sync-device-panel"
          className="absolute right-0 top-full mt-2 w-72 rounded-2xl p-4 z-50"
          style={{
            background: "rgba(26,26,62,0.98)",
            border: "1px solid rgba(139,92,246,0.3)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Connected Devices</h3>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
              <span className="text-xs text-white/60">{statusText}</span>
            </div>
          </div>

          {syncState.lastSyncAt && (
            <p className="text-xs text-white/40 mb-3">
              Last sync: {syncState.lastSyncAt.toLocaleTimeString()}
            </p>
          )}

          <div className="space-y-2">
            <DeviceEntry
              device={{
                id: "current",
                deviceId: syncState.deviceId,
                deviceName: "This device",
                deviceType: null,
                browser: null,
                os: null,
                lastActiveAt: new Date().toISOString(),
                isOnline: true,
                activeSessionId: null,
                activeView: null,
                childId: null,
              }}
              isCurrent
            />

            {syncState.otherDevices.map((device) => (
              <DeviceEntry key={device.id} device={device} />
            ))}
          </div>

          {syncState.activeChildren.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="text-xs text-white/50 mb-2">Active Sessions</p>
              {syncState.activeChildren.map((ac, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-white/70 py-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span>Learning on {ac.deviceName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeviceEntry({ device, isCurrent }: { device: DeviceInfo; isCurrent?: boolean }) {
  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
  const isOnline = device.isOnline && new Date(device.lastActiveAt) >= fiveMinAgo;

  return (
    <div
      data-testid={`device-entry-${device.deviceId}`}
      className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl"
      style={{
        background: isCurrent ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.03)",
        border: isCurrent ? "1px solid rgba(139,92,246,0.2)" : "1px solid transparent",
      }}
    >
      <div className="text-white/50">
        <DeviceIcon type={device.deviceType} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-white truncate">
            {device.deviceName || "Unknown device"}
          </span>
          {isCurrent && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(139,92,246,0.25)", color: "#C4B5FD" }}>
              current
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/40">
          {isOnline ? "Online" : `Last active ${timeAgo(device.lastActiveAt)}`}
        </span>
      </div>
      <div className={`w-2 h-2 rounded-full ${isOnline ? "bg-green-400" : "bg-white/20"}`} />
    </div>
  );
}
